import { useEffect } from "react";
import { useExecutingWorkflowsStore } from "@/stores/useExecutingWorkflowsStore";

type AddMessageListener =
  ((listener: (message: any) => void) => () => void) | undefined;

const STALE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export function useExecutionActivityListener(
  addMessageListener: AddMessageListener,
) {
  useEffect(() => {
    if (!addMessageListener) return;

    // Stale spinner cleanup interval
    const cleanupInterval = setInterval(() => {
      const store = useExecutingWorkflowsStore.getState();
      const now = Date.now();
      for (const [workflowId, entry] of store.executingWorkflows) {
        if (now - new Date(entry.startedAt).getTime() > STALE_TTL_MS) {
          store.markDone(workflowId);
        }
      }
    }, 60_000); // check every minute

    const removeListener = addMessageListener((message: any) => {
      const eventType = message?.eventType as string | undefined;
      if (!eventType) return;

      const workflowId = message.metadata?.workflowId as string | undefined;
      if (!workflowId) return;

      const store = useExecutingWorkflowsStore.getState();

      if (eventType === "workflow.started") {
        store.markExecuting(workflowId, {
          executionId: message.metadata?.executionId ?? "",
          projectId: message.metadata?.projectId,
          startedAt: message.eventData?.startedAt ?? new Date().toISOString(),
        });
      } else if (
        eventType === "workflow.completed" ||
        eventType === "workflow.failed" ||
        eventType === "workflow.cancelled"
      ) {
        store.markDone(workflowId);
      } else if (
        eventType === "node.started" ||
        eventType === "workflow.heartbeat"
      ) {
        // Fallback: if we missed workflow.started (e.g., page loaded mid-execution),
        // a node.started or heartbeat event seeds the store
        if (!store.executingWorkflows.has(workflowId)) {
          store.markExecuting(workflowId, {
            executionId: message.metadata?.executionId ?? "",
            projectId: message.metadata?.projectId,
            startedAt: new Date().toISOString(),
          });
        }
      }
    });

    return () => {
      removeListener();
      clearInterval(cleanupInterval);
    };
  }, [addMessageListener]);
}
